import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Trash2, Check, Users, MapPin, Clock as ClockIcon } from 'lucide-react';
import { toast } from 'sonner';
import { calcHoursDecimal, formatHours } from '@/lib/timeUtils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WorkerRapportinoForm() {
  const [cantieri, setCantieri] = useState([]);
  const [colleghi, setColleghi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cantiereMode, setCantiereMode] = useState('select');
  const [manualCantiere, setManualCantiere] = useState('');
  const [selectedColleghi, setSelectedColleghi] = useState([]);
  const [formData, setFormData] = useState({
    cantiere_id: '',
    date: new Date().toISOString().split('T')[0],
    hours_worked: '',
    ora_inizio: '',
    ora_fine: '',
    description: '',
    materials_used: [],
    signature_data: null,
    photo_urls: [],
    trasferta: false
  });
  const [newMaterial, setNewMaterial] = useState({ name: '', quantity: '', unit: 'pz' });
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, colleghiRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/colleghi`, { withCredentials: true })
      ]);
      const assigned = statsRes.data.assigned_cantieri || [];
      setCantieri(assigned);
      setColleghi(colleghiRes.data);
      if (assigned.length === 0) {
        setCantiereMode('manual');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setCantiereMode('manual');
    }
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    initCanvas();
  }, [initCanvas]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setFormData(prev => ({ ...prev, signature_data: canvas.toDataURL() }));
    }
  };

  const clearSignature = () => {
    initCanvas();
    setFormData(prev => ({ ...prev, signature_data: null }));
  };

  const addMaterial = () => {
    if (!newMaterial.name || !newMaterial.quantity) return;
    setFormData(prev => ({
      ...prev,
      materials_used: [...prev.materials_used, { ...newMaterial, quantity: parseFloat(newMaterial.quantity) }]
    }));
    setNewMaterial({ name: '', quantity: '', unit: 'pz' });
  };

  const removeMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      materials_used: prev.materials_used.filter((_, i) => i !== index)
    }));
  };

  const toggleCollega = (collega) => {
    setSelectedColleghi(prev => {
      const exists = prev.find(c => c.id === collega.id);
      if (exists) {
        return prev.filter(c => c.id !== collega.id);
      }
      return [...prev, collega];
    });
  };

  const calcHours = (inizio, fine) => {
    if (!inizio || !fine) return '';
    return calcHoursDecimal(inizio, fine);
  };

  const handleTimeChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    if (updated.ora_inizio && updated.ora_fine) {
      updated.hours_worked = calcHours(updated.ora_inizio, updated.ora_fine);
    }
    setFormData(prev => ({ ...prev, ...updated }));
  };

  const formatHoursDisplay = (val) => formatHours(val, formData.ora_inizio, formData.ora_fine);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cantiereValue = cantiereMode === 'manual' ? manualCantiere.trim() : formData.cantiere_id;
    if (!cantiereValue) {
      toast.error('Inserisci o seleziona un cantiere');
      return;
    }
    if (!formData.hours_worked) {
      toast.error('Inserisci le ore lavorate');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/rapportini`, {
        ...formData,
        cantiere_id: cantiereValue,
        hours_worked: parseFloat(formData.hours_worked),
        ora_inizio: formData.ora_inizio,
        ora_fine: formData.ora_fine,
        trasferta: formData.trasferta,
        colleghi: selectedColleghi.map(c => ({ id: c.id, name: c.name }))
      }, { withCredentials: true });
      toast.success('Rapportino inviato con successo');
      navigate('/worker/rapportini');
    } catch (error) {
      toast.error('Errore nell\'invio del rapportino');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="worker-rapportino-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/worker/rapportini')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold text-slate-900">Nuovo Rapportino</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Cantiere */}
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-4">
            <div className="space-y-2">
              <Label>Cantiere *</Label>
              <div className="flex gap-2 mb-2">
                {cantieri.length > 0 && (
                  <Button
                    type="button"
                    variant={cantiereMode === 'select' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setCantiereMode('select'); setManualCantiere(''); }}
                    className={cantiereMode === 'select' ? 'bg-slate-900 text-white' : ''}
                    data-testid="cantiere-mode-select"
                  >
                    Seleziona
                  </Button>
                )}
                <Button
                  type="button"
                  variant={cantiereMode === 'manual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setCantiereMode('manual'); setFormData(prev => ({...prev, cantiere_id: ''})); }}
                  className={cantiereMode === 'manual' ? 'bg-slate-900 text-white' : ''}
                  data-testid="cantiere-mode-manual"
                >
                  Scrivi manualmente
                </Button>
              </div>

              {cantiereMode === 'select' && cantieri.length > 0 ? (
                <Select value={formData.cantiere_id} onValueChange={(v) => setFormData(prev => ({...prev, cantiere_id: v}))}>
                  <SelectTrigger data-testid="cantiere-select">
                    <SelectValue placeholder="Seleziona cantiere" />
                  </SelectTrigger>
                  <SelectContent>
                    {cantieri.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={manualCantiere}
                  onChange={(e) => setManualCantiere(e.target.value)}
                  placeholder="Scrivi il nome del cantiere..."
                  data-testid="cantiere-manual-input"
                />
              )}
              {cantiereMode === 'manual' && (
                <p className="text-xs text-slate-400">Puoi inserire un cantiere anche se non ancora presente nel sistema</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({...prev, date: e.target.value}))}
                data-testid="date-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Dalle ore *</Label>
                <Input
                  type="time"
                  value={formData.ora_inizio}
                  onChange={(e) => handleTimeChange('ora_inizio', e.target.value)}
                  data-testid="ora-inizio-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Alle ore *</Label>
                <Input
                  type="time"
                  value={formData.ora_fine}
                  onChange={(e) => handleTimeChange('ora_fine', e.target.value)}
                  data-testid="ora-fine-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tot. Ore</Label>
                <div className="flex h-10 w-full items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold" data-testid="hours-display">
                  {formatHoursDisplay(formData.hours_worked)}
                </div>
              </div>
            </div>
            {formData.ora_inizio && formData.ora_fine && formData.hours_worked && (
              <p className="text-xs text-slate-400">Orario: {formData.ora_inizio} - {formData.ora_fine} ({formatHoursDisplay(formData.hours_worked)})</p>
            )}

            {/* Trasferta & Straordinari */}
            <div className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <label className="flex items-center gap-2 cursor-pointer flex-1" data-testid="trasferta-label">
                <Checkbox
                  checked={formData.trasferta}
                  onCheckedChange={(checked) => setFormData(prev => ({...prev, trasferta: !!checked}))}
                  data-testid="trasferta-checkbox"
                />
                <MapPin className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium text-slate-700">Trasferta</span>
              </label>
              {formData.hours_worked && parseFloat(formData.hours_worked) > 9 && (
                <div className="flex items-center gap-1.5 text-sm" data-testid="straordinari-display">
                  <ClockIcon className="h-4 w-4 text-red-500" />
                  <span className="font-medium text-red-600">
                    {(parseFloat(formData.hours_worked) - 9).toFixed(1)}h straordinari
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Descrizione Lavori *</Label>
              <Textarea
                placeholder="Descrivi i lavori eseguiti..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                className="min-h-[100px]"
                required
                data-testid="description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Colleghi */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Colleghi Presenti
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {colleghi.length === 0 ? (
              <p className="text-sm text-slate-400 py-2">Nessun altro operaio registrato</p>
            ) : (
              <div className="space-y-2">
                {colleghi.map((collega) => {
                  const isSelected = selectedColleghi.some(c => c.id === collega.id);
                  return (
                    <label
                      key={collega.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`collega-${collega.id}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleCollega(collega)}
                      />
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center">
                          <span className="text-xs font-medium text-slate-600">{collega.name?.charAt(0)}</span>
                        </div>
                        <span className="text-sm font-medium text-slate-900">{collega.name}</span>
                      </div>
                    </label>
                  );
                })}
                {selectedColleghi.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1">{selectedColleghi.length} collega/hi selezionato/i</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Materiali */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Materiali Utilizzati</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            {formData.materials_used.map((material, index) => (
              <div key={`${material.name}-${material.unit}-${index}`} className="flex items-center justify-between p-2 bg-slate-50 rounded border border-slate-200">
                <span className="text-sm">{material.name} - {material.quantity} {material.unit}</span>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeMaterial(index)} className="h-8 w-8 text-red-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                placeholder="Nome materiale"
                value={newMaterial.name}
                onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                className="flex-1"
                data-testid="material-name-input"
              />
              <Input
                type="number"
                placeholder="Qt"
                value={newMaterial.quantity}
                onChange={(e) => setNewMaterial({...newMaterial, quantity: e.target.value})}
                className="w-20"
                data-testid="material-qty-input"
              />
              <Select value={newMaterial.unit} onValueChange={(v) => setNewMaterial({...newMaterial, unit: v})}>
                <SelectTrigger className="w-20" data-testid="material-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pz">pz</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="m">m</SelectItem>
                  <SelectItem value="lt">lt</SelectItem>
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={addMaterial} data-testid="add-material-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Firma */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Firma Digitale</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="relative border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
              <canvas
                ref={canvasRef}
                width={300}
                height={150}
                className="w-full touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                data-testid="signature-canvas"
              />
              {formData.signature_data && (
                <div className="absolute top-2 right-2">
                  <Check className="h-5 w-5 text-emerald-500" />
                </div>
              )}
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={clearSignature} className="mt-2" data-testid="clear-signature-btn">
              Cancella firma
            </Button>
          </CardContent>
        </Card>

        {/* Submit */}
        <Button
          type="submit"
          className="w-full h-12 bg-slate-900 text-white hover:bg-slate-800 text-base"
          disabled={loading}
          data-testid="submit-rapportino-btn"
        >
          {loading ? 'Invio in corso...' : 'Invia Rapportino'}
        </Button>
      </form>
    </div>
  );
}
